import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import Sidebar from '@/components/Sidebar';
import CandidateCard, { Candidate } from '@/components/CandidateCard';
import { Plus, Search, Filter, UserPlus, Mail, Download, Users, Flag, Loader2, ArrowUpDown, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from "@/components/ui/badge";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSelectCandidate } from '@/hooks/useSelectCandidate';
import CandidateSheet from '@/components/candidate/CandidateSheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { startAtsApplicantsSync } from '@/services/knitService';

const Candidates: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [assessments, setAssessments] = useState<{id: string, title: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssessment, setSelectedAssessment] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedFitStatus, setSelectedFitStatus] = useState<string>('all');
  const [showAIFlaggedOnly, setShowAIFlaggedOnly] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'score'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [hasActiveIntegration, setHasActiveIntegration] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const lastFetchedRef = useRef<number>(0);
  const REFETCH_THRESHOLD = 30000;
  
  const {
    selectedCandidate,
    candidateAssessment,
    handleCandidateClick,
    handleCloseCandidate
  } = useSelectCandidate();

  useEffect(() => {
    if (user) {
      fetchCandidates();
      fetchAssessments();
      checkActiveIntegration();
    }
  }, [user]);

  // Refetch data when the tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        const now = Date.now();
        if (now - lastFetchedRef.current > REFETCH_THRESHOLD) {
          fetchCandidates();
          fetchAssessments();
          checkActiveIntegration();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup listener on component unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]); // Dependency on user

  // Function to check if the organization has an active Knit integration
  const checkActiveIntegration = async () => {
    try {
      // First get the user's organization ID
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user?.id)
        .single();
        
      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        return;
      }
      
      if (!profile?.organization_id) {
        // console.log("User has no organization ID");
        return;
      }
      
      // Check if there's an active integration for this organization
      const { data: integrations, error: integrationsError } = await supabase
        .from('knit_integrations')
        .select('id')
        .eq('organization_id', profile.organization_id)
        .eq('active', true)
        .limit(1);
        
      if (integrationsError) {
        console.error("Error fetching integrations:", integrationsError);
        return;
      }
      
      setHasActiveIntegration(integrations !== null && integrations.length > 0);
      // console.log("Organization has active integration:", hasActiveIntegration);
    } catch (error) {
      console.error("Error checking for active integrations:", error);
    }
  };

  // Function to handle syncing ATS applicants
  const handleSyncApplicants = async () => {
    try {
      // Record the start time
      const startTime = Date.now();
      
      // Show loading state
      setSyncLoading(true);
      
      // Make the API call
      const result = await startAtsApplicantsSync();
      
      if (result.success) {
        // Calculate how much time has passed since we started
        const elapsedTime = Date.now() - startTime;
        const requiredWaitTime = 5000; // 5 seconds
        
        // If less than 5 seconds have passed, wait for the remaining time
        if (elapsedTime < requiredWaitTime) {
          await new Promise(resolve => setTimeout(resolve, requiredWaitTime - elapsedTime));
        }
        
        // Show success message with guidance
        toast.success(
          <div className="flex flex-col gap-1">
            <p><strong>ATS applicants sync initiated!</strong></p>
            <p className="text-sm">Your applicants are being processed in the background.</p>
            <p className="text-sm">Please wait a few moments and refresh the page to see updates.</p>
          </div>,
          { 
            duration: 8000, // Show toast for 8 seconds
            action: {
              label: "Refresh Now",
              onClick: () => window.location.reload()
            }
          }
        );
        
        // Refresh candidates list after sync
        await fetchCandidates();
      } else {
        toast.error(result.message);
      }
    } catch (error: any) {
      console.error('Error syncing ATS applicants:', error);
      toast.error('Error syncing ATS applicants: ' + (error.message || 'Unknown error'));
    } finally {
      setSyncLoading(false);
    }
  };

  const fetchAssessments = async () => {
    try {
      const { data, error } = await supabase
        .from('assessments')
        .select('id, title')
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      setAssessments(data || []);
    } catch (error: any) {
      console.error('Error fetching assessments:', error);
      toast.error('Failed to load assessments');
    }
  };

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      
      // First get all assessments for the current user
      let userAssessments;
      let assessmentsError;
      
      try {
        // First attempt
        const result = await supabase
          .from('assessments')
          .select('id')
          .eq('user_id', user?.id);
        
        userAssessments = result.data;
        assessmentsError = result.error;
        
        // If we got an unauthorized error, try refreshing the token and retry
        if (assessmentsError && (assessmentsError.message?.includes('JWT expired') || assessmentsError.code === '401')) {
          // console.log('Token expired, refreshing and retrying...');
          
          // Refresh the session token
          const { data: { session: refreshedSession }, error: refreshError } = 
            await supabase.auth.refreshSession();
            
          if (!refreshError && refreshedSession) {
            // Retry the request after token refresh
            const retryResult = await supabase
              .from('assessments')
              .select('id')
              .eq('user_id', user?.id);
              
            userAssessments = retryResult.data;
            assessmentsError = retryResult.error;
          }
        }
      } catch (err) {
        console.error("Error in assessments fetch:", err);
        assessmentsError = err;
      }
      
      if (assessmentsError) throw assessmentsError;
      
      if (!userAssessments || userAssessments.length === 0) {
        setLoading(false);
        setCandidates([]);
        return;
      }
      
      const assessmentIds = userAssessments.map(a => a.id);
      
      // Then get all candidates for those assessments
      let candidatesData;
      let candidatesError;
      
      try {
        // First attempt
        const result = await supabase
          .from('candidates')
          .select('*, assessments(title, source)')
          .in('assessment_id', assessmentIds)
          .order('created_at', { ascending: false });
        
        candidatesData = result.data;
        candidatesError = result.error;
        
        // If we got an unauthorized error, try refreshing the token and retry
        if (candidatesError && (candidatesError.message?.includes('JWT expired') || candidatesError.code === '401')) {
          // console.log('Token expired during candidates fetch, refreshing and retrying...');
          
          // Refresh the session token
          const { data: { session: refreshedSession }, error: refreshError } = 
            await supabase.auth.refreshSession();
            
          if (!refreshError && refreshedSession) {
            // Retry the request after token refresh
            const retryResult = await supabase
              .from('candidates')
              .select('*, assessments(title, source)')
              .in('assessment_id', assessmentIds)
              .order('created_at', { ascending: false });
              
            candidatesData = retryResult.data;
            candidatesError = retryResult.error;
          }
        }
      } catch (err) {
        console.error("Error in candidates fetch:", err);
        candidatesError = err;
      }
      
      if (candidatesError) throw candidatesError;
      
      // Transform the data to match the Candidate interface
      const formattedCandidates: Candidate[] = (candidatesData || []).map(candidate => ({
        id: candidate.id,
        name: candidate.name,
        email: candidate.email,
        assessmentTitle: candidate.assessments?.title || 'Unknown Assessment',
        status: candidate.status as 'invited' | 'in_progress' | 'completed',
        completedDate: candidate.completed_date,
        created_at: candidate.created_at,
        displayDate: candidate.completed_date ? new Date(candidate.completed_date).toLocaleDateString() : undefined,
        score: candidate.score,
        aiRating: candidate.ai_rating,
        potentialPlagiarism: candidate.potential_plagiarism,
        source: candidate.source || candidate.assessments?.source || 'RoundOne', // Use candidate source first, fallback to assessment source, then default
        fit_status: candidate.fit_status
      }));
      
      setCandidates(formattedCandidates);
      
      // Update the last fetched timestamp
      lastFetchedRef.current = Date.now();
    } catch (error: any) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };
  
  // Get unique assessment titles for the filter
  const uniqueAssessments = Array.from(new Set(candidates.map(c => c.assessmentTitle)));
  
  const filterCandidates = () => {
    let filteredList = [...candidates];
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredList = filteredList.filter(
        candidate => 
          candidate.name.toLowerCase().includes(query) || 
          candidate.email.toLowerCase().includes(query) ||
          candidate.assessmentTitle.toLowerCase().includes(query)
      );
    }
    
    // Filter by assessment
    if (selectedAssessment !== 'all') {
      filteredList = filteredList.filter(
        candidate => candidate.assessmentTitle === selectedAssessment
      );
    }
    
    // Filter by status
    if (selectedStatus !== 'all') {
      filteredList = filteredList.filter(
        candidate => candidate.status === selectedStatus
      );
    }
    
    // Filter by fit status
    if (selectedFitStatus !== 'all') {
      if (selectedFitStatus === 'not_rated') {
        // Filter for candidates with no fit status
        filteredList = filteredList.filter(
          candidate => !candidate.fit_status
        );
      } else {
        // Filter for candidates with the selected fit status
        filteredList = filteredList.filter(
          candidate => candidate.fit_status === selectedFitStatus
        );
      }
    }
    
    // Filter by AI flagged
    if (showAIFlaggedOnly) {
      filteredList = filteredList.filter(
        candidate => candidate.potentialPlagiarism
      );
    }
    
    // Sort the filtered list
    filteredList.sort((a, b) => {
      if (sortBy === 'name') {
        return sortOrder === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (sortBy === 'score') {
        // First try to use aiRating, fallback to score if aiRating is not available
        const valueA = (a.aiRating !== undefined && a.aiRating !== null) ? a.aiRating : (a.score || 0);
        const valueB = (b.aiRating !== undefined && b.aiRating !== null) ? b.aiRating : (b.score || 0);
        return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
      } else { // date
        // For date sorting, first try completedDate, then fall back to created_at
        const dateA = a.completedDate ? new Date(a.completedDate).getTime() : 
                    (a.created_at ? new Date(a.created_at).getTime() : 0);
        const dateB = b.completedDate ? new Date(b.completedDate).getTime() : 
                    (b.created_at ? new Date(b.created_at).getTime() : 0);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      }
    });
    
    return filteredList;
  };

  const handleInviteCandidate = () => {
    if (assessments.length === 0) {
      toast.error('You need to create an assessment first before inviting candidates');
      return;
    }
    
    // Navigate to the first assessment detail page
    navigate(`/assessments/${assessments[0].id}`);
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 overflow-y-auto">
        <div className="p-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 animate-fade-in">
            <div>
              <h1 className="text-3xl font-bold text-roundone-dark-purple mb-2">Candidates</h1>
              <p className="text-muted-foreground">Manage and track all of your candidates</p>
            </div>
            <div className="flex gap-3 mt-4 sm:mt-0">
              {hasActiveIntegration && (
                <Button 
                  variant="outline" 
                  onClick={handleSyncApplicants}
                  disabled={syncLoading}
                  className="flex items-center"
                >
                  {syncLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Sync ATS Applicants
                </Button>
              )}
              <Button className="gradient-button" onClick={handleInviteCandidate}>
                <UserPlus size={16} className="mr-2" /> Invite Candidate
              </Button>
            </div>
          </div>
          
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Filter Candidates</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search candidates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:w-[500px]">
                  <Select value={selectedAssessment} onValueChange={setSelectedAssessment}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by assessment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Assessments</SelectItem>
                      {uniqueAssessments.map((assessment) => (
                        <SelectItem key={assessment} value={assessment}>
                          {assessment}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="h-10 w-10 relative"
                        >
                          <Filter size={16} />
                          {selectedStatus !== 'all' && (
                            <span className="absolute -top-1 -right-1 h-3 w-3 bg-roundone-purple rounded-full" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSelectedStatus('all')}>
                          All Statuses {selectedStatus === 'all' && '✓'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelectedStatus('invited')}>
                          Invited {selectedStatus === 'invited' && '✓'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelectedStatus('in_progress')}>
                          In Progress {selectedStatus === 'in_progress' && '✓'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelectedStatus('completed')}>
                          Completed {selectedStatus === 'completed' && '✓'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="h-10 w-10 relative"
                        >
                          <ArrowUpDown size={16} />
                          {sortBy !== 'date' && (
                            <span className="absolute -top-1 -right-1 h-3 w-3 bg-roundone-purple rounded-full" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {
                          setSortBy('date');
                          setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                        }}>
                          Sort by Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          setSortBy('name');
                          setSortOrder(sortBy === 'name' ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'asc');
                        }}>
                          Sort by Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          setSortBy('score');
                          setSortOrder(sortBy === 'score' ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc');
                        }}>
                          Sort by Rating {sortBy === 'score' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button 
                      variant={showAIFlaggedOnly ? "purple" : "outline"} 
                      className="h-10 w-10"
                      onClick={() => setShowAIFlaggedOnly(!showAIFlaggedOnly)}
                    >
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Flag size={16} className={showAIFlaggedOnly ? "fill-white" : ""} />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Filter candidates that used AI during assessment</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      {showAIFlaggedOnly && (
                        <span className="absolute -top-1 -right-1 h-3 w-3 bg-roundone-purple rounded-full" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Additional Filters Row */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8 animate-slide-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex flex-wrap items-center gap-2">
              {(selectedStatus !== 'all' || selectedAssessment !== 'all' || showAIFlaggedOnly) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setSelectedStatus('all');
                    setSelectedAssessment('all');
                    setShowAIFlaggedOnly(false);
                  }}
                  className="text-xs"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
          
          {/* Candidates Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-roundone-purple" />
            </div>
          ) : filterCandidates().length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              {filterCandidates().map((candidate) => (
                <CandidateCard
                  key={candidate.id}
                  candidate={candidate}
                  onClick={handleCandidateClick}
                />
              ))}
            </div>
          ) : candidates.length > 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="rounded-full bg-muted p-4 mb-4">
                <Users size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No candidates found</h3>
              <p className="text-muted-foreground mb-6 max-w-md">
                We couldn't find any candidates matching your search criteria.
              </p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchQuery('');
                  setSelectedStatus('all');
                  setSelectedAssessment('all');
                  setShowAIFlaggedOnly(false);
                }}
              >
                Reset Filters
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="rounded-full bg-muted p-4 mb-4">
                <Users size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No candidates yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md">
                You haven't invited any candidates yet. Create an assessment and invite candidates to get started.
              </p>
              <Button 
                variant="purple" 
                onClick={() => navigate('/assessments/create')}
              >
                <Plus size={16} className="mr-2" /> Create Assessment
              </Button>
            </div>
          )}
        </div>
      </main>

      {selectedCandidate && (
        <CandidateSheet
          open={true}
          onOpenChange={(open) => !open && handleCloseCandidate()}
          candidate={selectedCandidate}
          candidateAssessment={candidateAssessment}
          onRefresh={fetchCandidates}
        />
      )}
    </div>
  );
};

export default Candidates;
